import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../api/api";
import Pagination from "../../../components/Pagination";
import Table from "../../../components/table";
import usePagination from "../../../utils/usePagination";
import Breadcrumbs from "../../../components/Breadcrumbs";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";

const Transactions = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [purchaseEntries, setPurchaseEntries] = useState([]);
  const [loadingEntries, setLoadingEntries] = useState(false);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        setLoading(true);

        if (!token) {
          console.error("❌ No token found. Redirecting to login...");
          navigate("/login");
          return;
        }

        const res = await api.get("/billing/transactions");

        const data = res.data;
        console.log("data", data);

        const formattedData = data.transactions.map((item) => ({
          id: item.enq_id,
          job_number: item.job_number,
          customer_name: item.client_company,
          enq_id: item.enq_id,
          created_by: item.quotation_accepted_at
            ? new Date(item.quotation_accepted_at).toLocaleDateString("en-IN")
            : "",
        }));

        setClients(formattedData);
      } catch (error) {
        console.error("❌ Error fetching transactions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [token, navigate]);

  const openPurchaseModal = async (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowPurchaseModal(true);
    setPurchaseEntries([]);
    setLoadingEntries(true);

    try {
      const entryRes = await api.get(`/purchase/by-enquiry/${enquiry.enq_id}`);
      setPurchaseEntries(entryRes.data.entries || []);
    } catch (err) {
      setPurchaseEntries([]);
    } finally {
      setLoadingEntries(false);
    }
  };

  const filteredBySearch = clients.filter((client) =>
    client.customer_name?.toLowerCase().includes(search.toLowerCase()),
  );

  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    currentItems,
    goToPage,
    handleItemsPerPageChange,
  } = usePagination(filteredBySearch, 5);

  const columns = [
    { label: "Job Number", render: (c) => c.job_number },
    { label: "Company Name", render: (c) => c.customer_name },
    { label: "Date", render: (c) => c.created_by },
    {
      label: "Purchase",
      render: (c) => (
        <div
          style={{ cursor: "pointer", color: "#1e305e" }}
          title="View Purchase Entries"
          onClick={() => openPurchaseModal(c)}
        >
          <ShoppingCartOutlinedIcon />
        </div>
      ),
    },
    {
      label: "Income",
      render: (c) => (
        <Link to={`/invoice-details/${c.id}/${c.enq_id}`}>
          <PaidOutlinedIcon />
        </Link>
      ),
    },
  ];

  return (
    <>
      <Breadcrumbs />

      <div className="mb-3 position-relative">
        <input
          type="search"
          className="form-control ps-5"
          placeholder="Search by Customer"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <SearchIcon
          style={{
            position: "absolute",
            left: "12px",
            top: "50%",
            transform: "translateY(-50%)",
            color: "#6b7280",
            width: "16px",
            height: "16px",
          }}
        />
      </div>

      <Table
        columns={columns}
        data={currentItems}
        loading={loading}
        emptyMessage="No Transactions found."
        itemsPerPage={itemsPerPage}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalItems}
          />
        }
      />

      {/* Purchase Entries Modal */}
      {showPurchaseModal && selectedEnquiry && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Purchase Entries - {selectedEnquiry.job_number}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPurchaseModal(false)}
                />
              </div>
              <div className="modal-body">
                {loadingEntries ? (
                  <div className="text-center py-4">Loading purchase entries...</div>
                ) : purchaseEntries.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    No purchase entries found for this enquiry.
                  </div>
                ) : (
                  <div className="list-group">
                    {purchaseEntries.map((entry) => (
                      <div
                        key={entry.purchase_invoice_id}
                        className="list-group-item d-flex justify-content-between align-items-center"
                      >
                        <div>
                          <strong>
                            {entry.purchase_invoice_number || `#${entry.purchase_invoice_id}`}
                          </strong>
                          <br />
                          <small className="text-muted">
                            {entry.purchase_invoice_date
                              ? new Date(entry.purchase_invoice_date).toLocaleDateString("en-IN")
                              : ""}
                          </small>
                        </div>
                        <Link
                          to={`/purchase-details/${entry.purchase_invoice_id}`}
                          className="btn btn-sm btn-primary"
                          onClick={() => setShowPurchaseModal(false)}
                        >
                          <EditOutlinedIcon style={{ fontSize: 16, marginRight: 4 }} />
                          Edit
                        </Link>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPurchaseModal(false);
                    navigate(`/purchase-details/new?enq_id=${selectedEnquiry.enq_id}`);
                  }}
                >
                  <AddCircleOutlinedIcon style={{ fontSize: 18, marginRight: 6 }} />
                  Add New Purchase Entry
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPurchaseModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showPurchaseModal) && (
        <div className="modal-backdrop fade show"></div>
      )}
    </>
  );
};

export default Transactions;
